import * as React from 'react';
import HomeIcon from '@mui/icons-material/Home';
import SportsIcon from '@mui/icons-material/Sports';
import DescriptionIcon from '@mui/icons-material/Description';
import SummarizeIcon from '@mui/icons-material/Summarize';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import PersonIcon from '@mui/icons-material/Person';
import GroupsIcon from '@mui/icons-material/Groups';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import EventIcon from '@mui/icons-material/Event';
import AppRegistrationIcon from '@mui/icons-material/AppRegistration';
import SportsSoccerIcon from "@mui/icons-material/SportsSoccer";
import SportsGymnasticsIcon from '@mui/icons-material/SportsGymnastics';
import GavelIcon from "@mui/icons-material/Gavel";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import BusinessIcon from '@mui/icons-material/Business';
import EditNoteIcon from '@mui/icons-material/EditNote';
import ReceiptIcon from '@mui/icons-material/Receipt';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import { Description } from '@mui/icons-material';

// Interface for breadcrumb items
export interface BreadcrumbItem {
  label: string;
  path?: string;
  icon?: React.ReactNode;
}

// Interface for breadcrumb route configuration
export interface BreadcrumbRouteConfig {
  pattern: string | RegExp;
  getBreadcrumbs: (params: Record<string, string>, data?: any) => BreadcrumbItem[];
}

// Function to extract params from URL based on pattern
export const extractParams = (pattern: string, path: string): Record<string, string> => {
  const params: Record<string, string> = {};
  
  // Convert pattern to regex with named capturing groups
  const patternParts = pattern.split('/').filter(Boolean);
  const pathParts = path.split('/').filter(Boolean);
  
  if (patternParts.length !== pathParts.length) {
    return params;
  }
  
  patternParts.forEach((part, index) => {
    if (part.startsWith(':')) {
      const paramName = part.slice(1);
      params[paramName] = pathParts[index];
    }
  });
  
  return params;
};

// Allgemeine Dashboard-Routen
const generalDashboardRoutes: BreadcrumbRouteConfig[] = [
  // Dashboard
  {
    pattern: '/dashboard',
    getBreadcrumbs: () => [
      {
        label: 'Dashboard',
        path: '/dashboard',
        icon: React.createElement(HomeIcon, { fontSize: 'small' })
      }
    ]
  },
  
  // Admin
  {
    pattern: '/admin',
    getBreadcrumbs: () => [
      {
        label: 'Dashboard',
        path: '/dashboard',
        icon: React.createElement(HomeIcon, { fontSize: 'small' })
      },
      {
        label: 'Administration',
        icon: React.createElement(AdminPanelSettingsIcon, { fontSize: 'small' })
      }
    ]
  },
  
  // Clubs
  {
    pattern: '/clubs',
    getBreadcrumbs: () => [
      {
        label: 'Dashboard',
        path: '/dashboard',
        icon: React.createElement(HomeIcon, { fontSize: 'small' })
      },
      {
        label: 'Vereine',
        icon: React.createElement(GroupsIcon, { fontSize: 'small' })
      }
    ]
  },
  
  // Club detail
  {
    pattern: '/clubs/:clubId',
    getBreadcrumbs: (params, data) => [
      {
        label: 'Dashboard',
        path: '/dashboard',
        icon: React.createElement(HomeIcon, { fontSize: 'small' })
      },
      {
        label: 'Vereine',
        path: '/clubs',
        icon: React.createElement(GroupsIcon, { fontSize: 'small' })
      },
      {
        label: data?.clubName || `Verein ${params.clubId}`,
        icon: React.createElement(GroupsIcon, { fontSize: 'small' })
      }
    ]
  },
  
  // Users
  {
    pattern: '/users',
    getBreadcrumbs: () => [
      {
        label: 'Dashboard',
        path: '/dashboard',
        icon: React.createElement(HomeIcon, { fontSize: 'small' })
      },
      {
        label: 'Benutzer',
        icon: React.createElement(PersonIcon, { fontSize: 'small' })
      }
    ]
  },
  
  // User detail
  {
    pattern: '/users/:userId',
    getBreadcrumbs: (params, data) => [
      {
        label: 'Dashboard',
        path: '/dashboard',
        icon: React.createElement(HomeIcon, { fontSize: 'small' })
      },
      {
        label: 'Benutzer',
        path: '/users',
        icon: React.createElement(PersonIcon, { fontSize: 'small' })
      },
      {
        label: data?.userName || `Benutzer ${params.userId}`,
        icon: React.createElement(PersonIcon, { fontSize: 'small' })
      }
    ]
  },
  
  // Competitions
  {
    pattern: '/competitions',
    getBreadcrumbs: () => [
      {
        label: 'Dashboard',
        path: '/dashboard',
        icon: React.createElement(HomeIcon, { fontSize: 'small' })
      },
      {
        label: 'Wettkämpfe',
        icon: React.createElement(EventIcon, { fontSize: 'small' })
      }
    ]
  },
  
  // Competition detail
  {
    pattern: '/competitions/:competitionId',
    getBreadcrumbs: (params, data) => [
      {
        label: 'Dashboard',
        path: '/dashboard',
        icon: React.createElement(HomeIcon, { fontSize: 'small' })
      },
      {
        label: 'Wettkämpfe',
        path: '/competitions',
        icon: React.createElement(EventIcon, { fontSize: 'small' })
      },
      {
        label: data?.competitionName || `Wettkampf ${params.competitionId}`,
        icon: React.createElement(EventIcon, { fontSize: 'small' })
      }
    ]
  },
  
  // Athletes
  {
    pattern: '/athletes',
    getBreadcrumbs: () => [
      {
        label: 'Dashboard',
        path: '/dashboard',
        icon: React.createElement(HomeIcon, { fontSize: 'small' })
      },
      {
        label: 'Sportler',
        icon: React.createElement(SportsIcon, { fontSize: 'small' })
      }
    ]
  },
  
  // Athlete detail
  {
    pattern: '/athletes/:athleteId',
    getBreadcrumbs: (params, data) => [
      {
        label: 'Dashboard',
        path: '/dashboard',
        icon: React.createElement(HomeIcon, { fontSize: 'small' })
      },
      {
        label: 'Sportler',
        path: '/athletes',
        icon: React.createElement(SportsIcon, { fontSize: 'small' })
      },
      {
        label: data?.athleteName || `Sportler ${params.athleteId}`,
        icon: React.createElement(SportsIcon, { fontSize: 'small' })
      }
    ]
  },
];

// Default breadcrumb config - kombiniert alle Routen
export const breadcrumbRoutes: BreadcrumbRouteConfig[] = [
  ...generalDashboardRoutes,
  // Competition Registration Sportler
  {
    pattern: '/club/athletes',
    getBreadcrumbs: (params, data) => [
      {
        label: 'Dashboard',
        path: '/dashboard',
        icon: React.createElement(HomeIcon, { fontSize: 'small' })
      },
      {
        label: 'Verein',
        icon: React.createElement(BusinessIcon, { fontSize: 'small' })
      },
      {
        label: 'Sportler',
        icon: React.createElement(SportsGymnasticsIcon, { fontSize: 'small' })
      }
    ]
  },
  {
    pattern: '/club/judges',
    getBreadcrumbs: (params, data) => [
      {
        label: 'Dashboard',
        path: '/dashboard',
        icon: React.createElement(HomeIcon, { fontSize: 'small' })
      },
      {
        label: 'Verein',
        icon: React.createElement(BusinessIcon, { fontSize: 'small' })
      },
      {
        label: 'Kampfrichter',
        icon: React.createElement(GavelIcon, { fontSize: 'small' })
      }
    ]
  },
  {
    pattern: '/club/officials',
    getBreadcrumbs: (params, data) => [
      {
        label: 'Dashboard',
        path: '/dashboard',
        icon: React.createElement(HomeIcon, { fontSize: 'small' })
      },
      {
        label: 'Verein',
        icon: React.createElement(BusinessIcon, { fontSize: 'small' })
      },
      {
        label: 'Offizielle',
        icon: React.createElement(PersonIcon, { fontSize: 'small' })
      }
    ]
  },
  {
    pattern: '/club/profile',
    getBreadcrumbs: (params, data) => [
      {
        label: 'Dashboard',
        path: '/dashboard',
        icon: React.createElement(HomeIcon, { fontSize: 'small' })
      },
      {
        label: 'Verein',
        icon: React.createElement(BusinessIcon, { fontSize: 'small' })
      },
      {
        label: 'Daten',
        icon: React.createElement(EditNoteIcon, { fontSize: 'small' })
      }
    ]
  },
  {
    pattern: '/competition-registration/:competitionId/athletes',
    getBreadcrumbs: (params, data) => [
      {
        label: 'Dashboard',
        path: '/dashboard',
        icon: React.createElement(HomeIcon, { fontSize: 'small' })
      },
      {
        label: `Meldung ${data?.competitionName ? `'${data.competitionName}'` : ''}`,
        icon: React.createElement(AppRegistrationIcon, { fontSize: 'small' })
      },
      {
        label: 'Sportler',
        icon: React.createElement(SportsGymnasticsIcon, { fontSize: 'small' })
      }
    ]
  },
  // Competition Registration Karis
  {
    pattern: '/competition-registration/:competitionId/judges',
    getBreadcrumbs: (params, data) => [
      {
        label: 'Dashboard',
        path: '/dashboard',
        icon: React.createElement(HomeIcon, { fontSize: 'small' })
      },
      {
        label: `Meldung ${data?.competitionName ? `'${data.competitionName}'` : ''}`,
        icon: React.createElement(AppRegistrationIcon, { fontSize: 'small' })
      },
      {
        label: 'Kampfrichter',
        icon: React.createElement(GavelIcon, { fontSize: 'small' })
      }
    ]
  },
  // Competition Registration Offizielle
  {
    pattern: '/competition-registration/:competitionId/officials',
    getBreadcrumbs: (params, data) => [
      {
        label: 'Dashboard',
        path: '/dashboard',
        icon: React.createElement(HomeIcon, { fontSize: 'small' })
      },
      {
        label: `Meldung ${data?.competitionName ? `'${data.competitionName}'` : ''}`,
        icon: React.createElement(AppRegistrationIcon, { fontSize: 'small' })
      },
      {
        label: 'Offizielle',
        icon: React.createElement(PersonIcon, { fontSize: 'small' })
      }
    ]
  },
  // Competition Registration Zusatz
  {
    pattern: '/competition-registration/:competitionId/additional',
    getBreadcrumbs: (params, data) => [
      {
        label: 'Dashboard',
        path: '/dashboard',
        icon: React.createElement(HomeIcon, { fontSize: 'small' })
      },
      {
        label: `Meldung ${data?.competitionName ? `'${data.competitionName}'` : ''}`,
        icon: React.createElement(AppRegistrationIcon, { fontSize: 'small' })
      },
      {
        label: 'Zusatzbuchungen',
        icon: React.createElement(ShoppingCartIcon, { fontSize: 'small' })
      }
    ]
  },
  // Competition Registration Summary
  {
    pattern: '/competition-registration/:competitionId/summary',
    getBreadcrumbs: (params, data) => [
      {
        label: 'Dashboard',
        path: '/dashboard',
        icon: React.createElement(HomeIcon, { fontSize: 'small' })
      },
      {
        label: `Meldung ${data?.competitionName ? `'${data.competitionName}'` : ''}`,
        icon: React.createElement(AppRegistrationIcon, { fontSize: 'small' })
      },
      {
        label: 'Zusammenfassung',
        icon: React.createElement(SummarizeIcon, { fontSize: 'small' })
      }
    ]
  },
  
  // Competition Registration (current view)
  {
    pattern: '/competition-registration/:competitionId/current-summary',
    getBreadcrumbs: (params, data) => [
      {
        label: 'Dashboard',
        path: '/dashboard',
        icon: React.createElement(HomeIcon, { fontSize: 'small' })
      },
      {
        label: `Meldung ${data?.competitionName ? `'${data.competitionName}'` : ''}`,
        icon: React.createElement(SportsIcon, { fontSize: 'small' })
      },
      {
        label: 'Aktuelle Meldung',
        icon: React.createElement(SummarizeIcon, { fontSize: 'small' })
      }
    ]
  },
  
  // Competition Registration (saved view)
  {
    pattern: '/competition-registration/:competitionId/saved-summary',
    getBreadcrumbs: (params, data) => [
      {
        label: 'Dashboard',
        path: '/dashboard',
        icon: React.createElement(HomeIcon, { fontSize: 'small' })
      },
      {
        label: `Meldung ${data?.competitionName ? `'${data.competitionName}'` : ''}`,
        icon: React.createElement(SportsIcon, { fontSize: 'small' })
      },
      {
        label: 'Gespeicherte Meldung',
        icon: React.createElement(SummarizeIcon, { fontSize: 'small' })
      }
    ]
  },
  
  // Competition Registration
  {
    pattern: '/competition-registration/:competitionId',
    getBreadcrumbs: (params, data) => [
      {
        label: 'Dashboard',
        path: '/dashboard',
        icon: React.createElement(HomeIcon, { fontSize: 'small' })
      },
      {
        label: `Meldung ${data?.competitionName ? `'${data.competitionName}'` : ''}`,
        icon: React.createElement(SportsIcon, { fontSize: 'small' })
      }
    ]
  },
  
  //Registered Competition
  //   registered-competition/67d3530f4f2250cab3b75195/

  
  {
    pattern: '/registered-competition/:competitionId',
    getBreadcrumbs: (params, data) => [
      {
        label: 'Dashboard',
        path: '/dashboard',
        icon: React.createElement(HomeIcon, { fontSize: 'small' })
      },
      {
        label: `Gemeldet ${data?.competitionName ? `'${data.competitionName}'` : ''}`,
        icon: React.createElement(SportsIcon, { fontSize: 'small' })
      }
    ]
  },

  {
    pattern: '/registered-competition/:competitionId/summary',
    getBreadcrumbs: (params, data) => [
      {
        label: 'Dashboard',
        path: '/dashboard',
        icon: React.createElement(HomeIcon, { fontSize: 'small' })
      },
      {
        label: `Gemeldet ${data?.competitionName ? `'${data.competitionName}'` : ''}`,
        icon: React.createElement(SportsIcon, { fontSize: 'small' })
      },
      {
        label: 'Zusammenfassung',
        icon: React.createElement(SummarizeIcon, { fontSize: 'small' })
      }
    ]
  },

  {
    pattern: '/registered-competition/:competitionId/cards',
    getBreadcrumbs: (params, data) => [
      {
        label: 'Dashboard',
        path: '/dashboard',
        icon: React.createElement(HomeIcon, { fontSize: 'small' })
      },
      {
        label: `Gemeldet ${data?.competitionName ? `'${data.competitionName}'` : ''}`,
        icon: React.createElement(SportsIcon, { fontSize: 'small' })
      },
      {
        label: 'Wettkampfkarten',
        icon: React.createElement(DescriptionIcon, { fontSize: 'small' })
      }
    ]
  },

  {
    pattern: '/registered-competition/:competitionId/schedule',
    getBreadcrumbs: (params, data) => [
      {
        label: 'Dashboard',
        path: '/dashboard',
        icon: React.createElement(HomeIcon, { fontSize: 'small' })
      },
      {
        label: `Gemeldet ${data?.competitionName ? `'${data.competitionName}'` : ''}`,
        icon: React.createElement(SportsIcon, { fontSize: 'small' })
      },
      {
        label: 'Startzeiten',
        icon: React.createElement(EventIcon, { fontSize: 'small' })
      }
    ]
  },

  // Registration Confirmation
  {
    pattern: '/orga/competition/:confirmationId',
    getBreadcrumbs: (params, data) => [
      {
        label: 'Dashboard',
        path: '/dashboard',
        icon: React.createElement(HomeIcon, { fontSize: 'small' })
      },
      {
        label: data?.competitionName ? `Wettkampf '${data.competitionName}'` : 'Wettkampf',
        path: data?.competitionId ? `/orga/competition/${data.competitionId}` : undefined,
        icon: React.createElement(CompareArrowsIcon, { fontSize: 'small' })
      }
    ]
  },
  
  // Registration Confirmation
  {
    pattern: '/orga/competition/:competitionId/confirmation/:confirmationId',
    getBreadcrumbs: (params, data) => [
      {
        label: 'Dashboard',
        path: '/dashboard',
        icon: React.createElement(HomeIcon, { fontSize: 'small' })
      },
      {
        label: data?.competitionName ? `Wettkampf '${data.competitionName}'` : 'Wettkampf',
        path: data?.competitionId ? `/orga/competition/${data.competitionId}` : undefined,
        icon: React.createElement(CompareArrowsIcon, { fontSize: 'small' })
      },
      {
        label: 'Meldebestätigung erstellen',
        icon: React.createElement(DescriptionIcon, { fontSize: 'small' })
      }
    ]
  },
  
  // Registration Confirmation
  {
    pattern: '/orga/competition/confirmation-registration/:confirmationId',
    getBreadcrumbs: (params, data) => [
      {
        label: 'Dashboard',
        path: '/dashboard',
        icon: React.createElement(HomeIcon, { fontSize: 'small' })
      },
      {
        label: data?.competitionName ? `Wettkampf '${data.competitionName}'` : 'Wettkampf',
        path: data?.competitionId ? `/orga/competition/${data.competitionId}` : undefined,
        icon: React.createElement(CompareArrowsIcon, { fontSize: 'small' })
      },
      {
        label: 'Meldebestätigung PDF',
        icon: React.createElement(PictureAsPdfIcon, { fontSize: 'small' })
      }
    ]
  },
  
  // Registration Confirmation
  {
    pattern: '/orga/competition/:competitionId/club/:clubId/saved-summary',
    getBreadcrumbs: (params, data) => [
      {
        label: 'Dashboard',
        path: '/dashboard',
        icon: React.createElement(HomeIcon, { fontSize: 'small' })
      },
      {
        label: data?.competitionName ? `Wettkampf '${data.competitionName}'` : 'Wettkampf',
        path: data?.competitionId ? `/orga/competition/${data.competitionId}` : undefined,
        icon: React.createElement(CompareArrowsIcon, { fontSize: 'small' })
      },
      {
        label: 'Gespeicherte Meldung',
        icon: React.createElement(SummarizeIcon, { fontSize: 'small' })
      }
    ]
  },
  
  // Registration Confirmation
  {
    pattern: '/orga/competition/:competitionId/club/:clubId/current-summary',
    getBreadcrumbs: (params, data) => [
      {
        label: 'Dashboard',
        path: '/dashboard',
        icon: React.createElement(HomeIcon, { fontSize: 'small' })
      },
      {
        label: data?.competitionName ? `Wettkampf '${data.competitionName}'` : 'Wettkampf',
        path: data?.competitionId ? `/orga/competition/${data.competitionId}` : undefined,
        icon: React.createElement(CompareArrowsIcon, { fontSize: 'small' })
      },
      {
        label: 'Aktuelle Meldung',
        icon: React.createElement(SummarizeIcon, { fontSize: 'small' })
      }
    ]
  },
  
  // PDF Ansicht for Registration Confirmation
  {
    pattern: '/orga/competition/:competitionId/confirmation-registration/:confirmationId',
    getBreadcrumbs: (params, data) => [
      {
        label: 'Dashboard',
        path: '/dashboard',
        icon: React.createElement(HomeIcon, { fontSize: 'small' })
      },
      {
        label: data?.competitionName ? `Wettkampf '${data.competitionName}'` : 'Wettkampf',
        path: data?.competitionId ? `/orga/competition/${data.competitionId}` : undefined,
        icon: React.createElement(CompareArrowsIcon, { fontSize: 'small' })
      },
      {
        label: 'Meldebestätigung',
        path: `/orga/competition/confirmation-registration/${params.confirmationId}`,
        icon: React.createElement(DescriptionIcon, { fontSize: 'small' })
      },
      {
        label: 'PDF Ansicht',
        icon: React.createElement(DescriptionIcon, { fontSize: 'small' })
      }
    ]
  },
  
  // Email Sending for Registration Confirmation
  {
    pattern: '/orga/competition/:competitionId/confirmation-registration/:confirmationId/send-email',
    getBreadcrumbs: (params, data) => [
      {
        label: 'Dashboard',
        path: '/dashboard',
        icon: React.createElement(HomeIcon, { fontSize: 'small' })
      },
      {
        label: data?.competitionName ? `Wettkampf '${data.competitionName}'` : 'Wettkampf',
        path: data?.competitionId ? `/orga/competition/${data.competitionId}` : undefined,
        icon: React.createElement(CompareArrowsIcon, { fontSize: 'small' })
      },
      {
        label: 'Meldebestätigung',
        path: `/orga/competition/confirmation-registration/${params.confirmationId}`,
        icon: React.createElement(DescriptionIcon, { fontSize: 'small' })
      },
      {
        label: 'E-Mail senden',
        icon: React.createElement(DescriptionIcon, { fontSize: 'small' })
      }
    ]
  }
];

// Generate breadcrumbs from path segments as fallback
export const generateBreadcrumbsFromPath = (path: string): BreadcrumbItem[] => {
  const segments = path.split('/').filter(Boolean);
  
  if (segments.length === 0) {
    return [
      {
        label: 'Dashboard',
        path: '/dashboard',
        icon: React.createElement(HomeIcon, { fontSize: 'small' })
      }
    ];
  }
  
  const breadcrumbs: BreadcrumbItem[] = [
    {
      label: 'Dashboard',
      path: '/dashboard',
      icon: React.createElement(HomeIcon, { fontSize: 'small' })
    }
  ];
  
  let currentPath = '';
  
  segments.forEach((segment, index) => {
    currentPath += `/${segment}`;
    
    // Map specific segments to better labels
    let label = segment.charAt(0).toUpperCase() + segment.slice(1);
    
    // Map specific paths to better labels
    if (segment === 'admin') label = 'Administration';
    if (segment === 'clubs') label = 'Vereine';
    if (segment === 'users') label = 'Benutzer';
    if (segment === 'competitions') label = 'Wettkämpfe';
    if (segment === 'athletes') label = 'Sportler';
    if (segment === 'judges') label = 'Kampfrichter';
    if (segment === 'officials') label = 'Offizielle';
    
    breadcrumbs.push({
      label,
      path: index < segments.length - 1 ? currentPath : undefined
    });
  });
  
  return breadcrumbs;
};

// Function to get breadcrumbs for a given path
export const getBreadcrumbsForPath = (
  path: string, 
  data?: any
): BreadcrumbItem[] => {
  // Find matching route config
  for (const route of breadcrumbRoutes) {
    if (typeof route.pattern === 'string') {
      // If pattern is a string, check for match by extracting params
      const patternRegex = new RegExp(
        `^${route.pattern.replace(/:[^/]+/g, '([^/]+)')}$`
      );
      
      if (patternRegex.test(path)) {
        const params = extractParams(route.pattern, path);
        return route.getBreadcrumbs(params, data);
      }
    } else if (route.pattern instanceof RegExp) {
      // If pattern is a RegExp, use it directly
      const match = path.match(route.pattern);
      if (match) {
        const params: Record<string, string> = {};
        return route.getBreadcrumbs(params, data);
      }
    }
  }
  
  // Fallback: generate breadcrumbs from path segments
  return generateBreadcrumbsFromPath(path);
};